import React, { useState } from 'react';

interface WindowControlsProps {
  winId: string;
  isMaximized: boolean;
  onMinimize: () => void;
  onMaximize: () => void;
  onClose: () => void;
  onSnapLayout?: (
    snapType:
      | 'left' | 'right' | 'top' | 'grid-tl' | 'grid-tr' | 'grid-bl' | 'grid-br'
      | 'col4-1' | 'col4-2' | 'col4-3' | 'col4-4'
      | 'col25-75-l' | 'col25-75-r'
      | 'col75-25-l' | 'col75-25-r'
      | 'col50-25-25-l' | 'col50-25-25-m' | 'col50-25-25-r'
      | 'col25-25-50-l' | 'col25-25-50-m' | 'col25-25-50-r'
  ) => void;
}

export const WindowControls: React.FC<WindowControlsProps> = ({
  winId,
  isMaximized,
  onMinimize,
  onMaximize,
  onClose,
  onSnapLayout,
}) => {
  const [showSnapPopup, setShowSnapPopup] = useState(false);
  let timeoutId: any = null;

  const handleMouseEnter = () => {
    if (timeoutId) clearTimeout(timeoutId);
    setShowSnapPopup(true);
  };

  const handleMouseLeave = () => {
    timeoutId = setTimeout(() => {
      setShowSnapPopup(false);
    }, 300);
  };

  const handleSnap = (type: any) => {
    onSnapLayout?.(type);
    setShowSnapPopup(false);
  };

  return (
    <div 
      className="flex items-center h-full relative"
      onMouseLeave={handleMouseLeave}
    >
      {/* 1. Botão Minimizar */}
      <button
        onClick={(e) => { e.stopPropagation(); onMinimize(); }}
        className="w-8 h-8 flex items-center justify-center text-[#a6adc8] hover:bg-[#313244]/55 hover:text-white transition-all cursor-pointer border-none bg-transparent active:scale-95 rounded-lg"
        title="Minimizar"
      >
        <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
          <rect x="3" y="11" width="18" height="2.5" rx="1" />
        </svg>
      </button>

      {/* 2. Botão Maximizar / Restaurar (com hover trigger) */}
      <div 
        className="relative h-full flex items-center"
        onMouseEnter={handleMouseEnter}
      >
        <button
          onClick={(e) => { e.stopPropagation(); onMaximize(); }}
          className="w-8 h-8 flex items-center justify-center text-[#a6adc8] hover:bg-[#313244]/55 hover:text-white transition-all cursor-pointer border-none bg-transparent active:scale-95 rounded-lg"
          title={isMaximized ? 'Restaurar' : 'Maximizar'}
        >
          {isMaximized ? (
            <svg className="w-3.5 h-3.5 fill-none stroke-current stroke-[2.5]" viewBox="0 0 24 24">
              <rect x="4" y="8" width="12" height="12" rx="1.5" />
              <path d="M8 8V4a1.5 1.5 0 0 1 1.5-1.5h12A1.5 1.5 0 0 1 23 4v12a1.5 1.5 0 0 1-1.5 1.5H17" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5 fill-none stroke-current stroke-[2.5]" viewBox="0 0 24 24">
              <rect x="4" y="4" width="16" height="16" rx="2" />
            </svg>
          )}
        </button>

        {/* Windows 11 Snap Layouts Hover Popup */}
        {showSnapPopup && (
          <div 
            onMouseEnter={handleMouseEnter}
            className="absolute top-9 left-1/2 -translate-x-1/2 bg-[#1e1e2e]/98 border border-[#313244] rounded-2xl p-3 shadow-[0_15px_40px_rgba(0,0,0,0.7)] flex flex-wrap gap-3 z-[999999] backdrop-blur-md pointer-events-auto select-none w-[310px] text-[#cdd6f4] justify-center"
          >
            {/* Metades */}
            <div className="flex flex-col gap-1 items-center w-[60px] flex-shrink-0">
              <span className="text-[6.5px] font-extrabold uppercase text-[#a6adc8] tracking-wider mb-0.5">Metades</span>
              <div className="w-14 h-10 border border-[#313244] rounded bg-[#181825] flex p-0.5 gap-0.5">
                <div 
                  onClick={() => handleSnap('left')}
                  className="flex-1 rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                  title="Esquerda"
                />
                <div 
                  onClick={() => handleSnap('right')}
                  className="flex-1 rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                  title="Direita"
                />
              </div>
            </div>

            {/* Grelha */}
            <div className="flex flex-col gap-1 items-center w-[60px] flex-shrink-0">
              <span className="text-[6.5px] font-extrabold uppercase text-[#a6adc8] tracking-wider mb-0.5">Grelha</span>
              <div className="w-14 h-10 border border-[#313244] rounded bg-[#181825] flex flex-col p-0.5 gap-0.5">
                <div className="flex-1 flex gap-0.5">
                  <div 
                    onClick={() => handleSnap('grid-tl')}
                    className="flex-1 rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                    title="Topo Esquerdo"
                  />
                  <div 
                    onClick={() => handleSnap('grid-tr')}
                    className="flex-1 rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                    title="Topo Direito"
                  />
                </div>
                <div className="flex-1 flex gap-0.5">
                  <div 
                    onClick={() => handleSnap('grid-bl')}
                    className="flex-1 rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                    title="Baixo Esquerdo"
                  />
                  <div 
                    onClick={() => handleSnap('grid-br')}
                    className="flex-1 rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                    title="Baixo Direito"
                  />
                </div>
              </div>
            </div>

            {/* 4 Colunas */}
            <div className="flex flex-col gap-1 items-center w-[60px] flex-shrink-0">
              <span className="text-[6.5px] font-extrabold uppercase text-[#a6adc8] tracking-wider mb-0.5">4 Colunas</span>
              <div className="w-14 h-10 border border-[#313244] rounded bg-[#181825] flex p-0.5 gap-0.5">
                <div 
                  onClick={() => handleSnap('col4-1')}
                  className="w-1/4 h-full rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                  title="Coluna 1"
                />
                <div 
                  onClick={() => handleSnap('col4-2')}
                  className="w-1/4 h-full rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                  title="Coluna 2"
                />
                <div 
                  onClick={() => handleSnap('col4-3')}
                  className="w-1/4 h-full rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                  title="Coluna 3"
                />
                <div 
                  onClick={() => handleSnap('col4-4')}
                  className="w-1/4 h-full rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                  title="Coluna 4"
                />
              </div>
            </div>

            {/* 25 / 75 */}
            <div className="flex flex-col gap-1 items-center w-[60px] flex-shrink-0">
              <span className="text-[6.5px] font-extrabold uppercase text-[#a6adc8] tracking-wider mb-0.5">25 / 75</span>
              <div className="w-14 h-10 border border-[#313244] rounded bg-[#181825] flex p-0.5 gap-0.5">
                <div 
                  onClick={() => handleSnap('col25-75-l')}
                  className="w-1/4 h-full rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                  title="Esquerda (25%)"
                />
                <div 
                  onClick={() => handleSnap('col25-75-r')}
                  className="w-3/4 h-full rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                  title="Direita (75%)"
                />
              </div>
            </div>

            {/* 75 / 25 */}
            <div className="flex flex-col gap-1 items-center w-[60px] flex-shrink-0">
              <span className="text-[6.5px] font-extrabold uppercase text-[#a6adc8] tracking-wider mb-0.5">75 / 25</span>
              <div className="w-14 h-10 border border-[#313244] rounded bg-[#181825] flex p-0.5 gap-0.5">
                <div 
                  onClick={() => handleSnap('col75-25-l')}
                  className="w-3/4 h-full rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                  title="Esquerda (75%)"
                />
                <div 
                  onClick={() => handleSnap('col75-25-r')}
                  className="w-1/4 h-full rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                  title="Direita (25%)"
                />
              </div>
            </div>

            {/* 50 / 25 / 25 */}
            <div className="flex flex-col gap-1 items-center w-[60px] flex-shrink-0">
              <span className="text-[6.5px] font-extrabold uppercase text-[#a6adc8] tracking-wider mb-0.5">50/25/25</span>
              <div className="w-14 h-10 border border-[#313244] rounded bg-[#181825] flex p-0.5 gap-0.5">
                <div 
                  onClick={() => handleSnap('col50-25-25-l')}
                  className="w-1/2 h-full rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                  title="Esquerda (50%)"
                />
                <div 
                  onClick={() => handleSnap('col50-25-25-m')}
                  className="w-1/4 h-full rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                  title="Meio (25%)"
                />
                <div 
                  onClick={() => handleSnap('col50-25-25-r')}
                  className="w-1/4 h-full rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                  title="Direita (25%)"
                />
              </div>
            </div>

            {/* 25 / 25 / 50 */}
            <div className="flex flex-col gap-1 items-center w-[60px] flex-shrink-0">
              <span className="text-[6.5px] font-extrabold uppercase text-[#a6adc8] tracking-wider mb-0.5">25/25/50</span>
              <div className="w-14 h-10 border border-[#313244] rounded bg-[#181825] flex p-0.5 gap-0.5">
                <div 
                  onClick={() => handleSnap('col25-25-50-l')}
                  className="w-1/4 h-full rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                  title="Esquerda (25%)"
                />
                <div 
                  onClick={() => handleSnap('col25-25-50-m')}
                  className="w-1/4 h-full rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                  title="Meio (25%)"
                />
                <div 
                  onClick={() => handleSnap('col25-25-50-r')}
                  className="w-1/2 h-full rounded bg-[#313244]/40 hover:bg-[#cba6f7]/30 border border-transparent hover:border-[#cba6f7] transition-all cursor-pointer"
                  title="Direita (50%)"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 3. Botão Fechar */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="w-8 h-8 flex items-center justify-center text-[#a6adc8] hover:bg-red-500 hover:text-white transition-all cursor-pointer border-none bg-transparent active:scale-95 rounded-lg"
        title="Fechar"
      >
        <svg className="w-3.5 h-3.5 fill-none stroke-current stroke-[2.5]" viewBox="0 0 24 24">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};
